import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ReceiptIndianRupee, RefreshCw, FileDown, FileCheck2, BadgeCheck, Banknote, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { onErr, downloadFile, inr, num, thisMonth, todayStr } from "@/lib/providers";

export default function PayoutsTab() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonth());
  const [invFor, setInvFor] = useState(null);
  const [inv, setInv] = useState({ number: "", date: todayStr(), file: null });
  const [paidFor, setPaidFor] = useState(null);
  const [paid, setPaid] = useState({ ref: "", date: todayStr() });

  const { data: rows, isLoading } = useQuery({ queryKey: ["payouts", month], queryFn: async () => (await api.get("/provider-payouts", { params: { month } })).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["payouts"] }); qc.invalidateQueries({ queryKey: ["workOrders"] }); };

  const generate = useMutation({
    mutationFn: async () => (await api.post("/provider-payouts/generate", { month })).data,
    onSuccess: (d) => { toast.success(d.generated ? `${d.generated} payout advice(s) prepared` : "Nothing new to bill — no unbilled verified logs (or next milestone not reached)"); refresh(); },
    onError: onErr,
  });
  const recordInv = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("invoice_number", inv.number); fd.append("invoice_date", inv.date);
      if (inv.file) fd.append("file", inv.file);
      return (await api.post(`/provider-payouts/${invFor.id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { toast.success("Invoice recorded"); refresh(); setInvFor(null); },
    onError: onErr,
  });
  const approve = useMutation({
    mutationFn: async (id) => (await api.post(`/provider-payouts/${id}/approve`)).data,
    onSuccess: () => { toast.success("Approved for payment"); refresh(); },
    onError: onErr,
  });
  const markPaid = useMutation({
    mutationFn: async () => (await api.post(`/provider-payouts/${paidFor.id}/paid`, { payment_reference: paid.ref, paid_on: paid.date })).data,
    onSuccess: () => { toast.success("Marked paid"); refresh(); setPaidFor(null); },
    onError: onErr,
  });

  const totals = (rows || []).reduce((a, r) => ({ value: a.value + r.taxable_value, gst: a.gst + r.gst_amount, tds: a.tds + r.tds_amount, net: a.net + r.net_payable }), { value: 0, gst: 0, tds: 0, net: 0 });

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div><Label>Month</Label><Input type="month" className="mt-1 w-44" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
        <Button className="bg-navy hover:bg-navy-700" disabled={generate.isPending} onClick={() => generate.mutate()}><RefreshCw className="h-4 w-4 mr-2" /> Prepare payout advices</Button>
        <p className="text-xs text-slate-500 max-w-md ml-auto">Bills every verified, unbilled log up to month-end. Re-running replaces Draft advices only. Approve needs the provider's own invoice.</p>
      </div>
      {isLoading ? <Loading /> : !rows?.length ? (
        <EmptyState icon={ReceiptIndianRupee} title="No payout advices for this month" hint="Verify field logs, then click Prepare payout advices." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Advice", "Provider", "Units", "Service value", "GST", "TDS", "Net payable", "Invoice", "Status", ""].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-sm text-teal">{r.advice_number}<div className="text-xs text-slate-400">{r.wo_number}</div></TableCell>
                  <TableCell className="font-medium text-navy">{r.provider_name}<div className="text-xs text-slate-400">{r.provider_code}</div></TableCell>
                  <TableCell className="text-sm">{num(r.billable_units)}{r.excess_units > 0 && <div className="text-xs text-amber-700">+{num(r.excess_units)} over target</div>}</TableCell>
                  <TableCell className="text-sm">{inr(r.taxable_value)}</TableCell>
                  <TableCell className="text-sm">{r.gst_amount ? inr(r.gst_amount) : "—"}</TableCell>
                  <TableCell className="text-sm">{inr(r.tds_amount)}<div className="text-xs text-slate-400">{r.tds_section} {Math.round(r.tds_rate * 1000) / 10}%</div></TableCell>
                  <TableCell className="text-sm font-semibold text-navy">{inr(r.net_payable)}</TableCell>
                  <TableCell className="text-xs">
                    {r.invoice_number ? <>{r.invoice_number}<div className="text-slate-400">{r.invoice_date}</div></> : <span className="text-amber-700">Awaiting</span>}
                    {r.has_invoice_file && <Button size="sm" variant="ghost" title="Invoice file" onClick={() => downloadFile(`/provider-payouts/${r.id}/invoice-file`, `Invoice_${r.advice_number}`)}><Paperclip className="h-3.5 w-3.5" /></Button>}
                  </TableCell>
                  <TableCell><StatusBadge status={r.status} />{r.status === "Paid" && <div className="text-[11px] text-slate-400">{r.paid_on} · {r.payment_reference}</div>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" title="Payout advice PDF" onClick={() => downloadFile(`/provider-payouts/${r.id}/pdf`, `PayoutAdvice_${r.advice_number}.pdf`)}><FileDown className="h-4 w-4" /></Button>
                    {r.status === "Draft" && <>
                      <Button size="sm" variant="ghost" title="Record provider invoice" onClick={() => { setInvFor(r); setInv({ number: r.invoice_number || "", date: r.invoice_date || todayStr(), file: null }); }}><FileCheck2 className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-emerald-700" title="Approve" disabled={!r.invoice_number} onClick={() => approve.mutate(r.id)}><BadgeCheck className="h-4 w-4" /></Button>
                    </>}
                    {r.status === "Approved" && <Button size="sm" variant="ghost" className="text-teal" title="Mark paid" onClick={() => { setPaidFor(r); setPaid({ ref: "", date: todayStr() }); }}><Banknote className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={3}>Total</TableCell>
                <TableCell>{inr(totals.value)}</TableCell><TableCell>{inr(totals.gst)}</TableCell><TableCell>{inr(totals.tds)}</TableCell>
                <TableCell className="text-navy">{inr(totals.net)}</TableCell><TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!invFor} onOpenChange={(o) => !o && setInvFor(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Provider invoice — {invFor?.advice_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Invoice should be for {inr(invFor?.invoice_total)} ({inr(invFor?.taxable_value)}{invFor?.gst_amount ? ` + GST ${inr(invFor?.gst_amount)}` : ""}).</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Invoice no. *</Label><Input className="mt-1" value={inv.number} onChange={(e) => setInv({ ...inv, number: e.target.value })} /></div>
            <div><Label>Invoice date *</Label><Input type="date" className="mt-1" value={inv.date} onChange={(e) => setInv({ ...inv, date: e.target.value })} /></div>
          </div>
          <div><Label>Invoice copy (PDF/JPG)</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png" className="mt-1" onChange={(e) => setInv({ ...inv, file: e.target.files?.[0] || null })} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!inv.number || !inv.date || recordInv.isPending} onClick={() => recordInv.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paidFor} onOpenChange={(o) => !o && setPaidFor(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Mark {paidFor?.advice_number} paid — {inr(paidFor?.net_payable)}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Payment reference (UTR) *</Label><Input className="mt-1" value={paid.ref} onChange={(e) => setPaid({ ...paid, ref: e.target.value })} /></div>
            <div><Label>Paid on *</Label><Input type="date" className="mt-1" value={paid.date} onChange={(e) => setPaid({ ...paid, date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={paid.ref.length < 2 || markPaid.isPending} onClick={() => markPaid.mutate()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
